// Confezione regalo con biglietto.
// - Mostra la casella e il riquadro del biglietto nella scheda prodotto.
// - Quando la casella e' spuntata, aggiunge al carrello l'articolo e la confezione in un'unica richiesta.
// - Se l'articolo viene tolto o cambia quantita' nel carrello, allinea la confezione.
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const FLAG = 'Confezione regalo';
const NOTE = 'Biglietto';
const FOR_VARIANT = '_Per_variante';
const MAX_NOTE = 150;
// Proprieta' nascosta con valore unico: tiene separate le righe con confezione, ognuna col proprio biglietto.
const ROW = '_Riga';
const newToken = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

function buildAddPayload(entries, giftVariantId, productTitle, wrapNotes) {
  const base = {};
  let id = 0;
  let quantity = 1;
  const sections = new Set();

  for (const [key, raw] of entries) {
    const value = typeof raw === 'string' ? raw : '';
    const match = key.match(/^properties\[(.+)\]$/);
    if (match) {
      const name = match[1];
      if (name !== FLAG && name !== NOTE && value.trim() !== '') base[name] = value.trim();
      continue;
    }
    if (key === 'id') id = Number(value);
    else if (key === 'quantity') quantity = Math.max(1, parseInt(value, 10) || 1);
    else if (key === 'sections') {
      value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => sections.add(s));
    }
  }

  const notes = (Array.isArray(wrapNotes) ? wrapNotes : []).slice(0, quantity);
  if (!id || notes.length === 0) return null;

  const items = [];
  const plain = quantity - notes.length;
  if (plain > 0) items.push({ id, quantity: plain, properties: { ...base } });
  for (const raw of notes) {
    const props = { ...base, [FLAG]: 'Sì', [ROW]: newToken() };
    const note = String(raw || '').trim().slice(0, MAX_NOTE);
    if (note) props[NOTE] = note;
    items.push({ id, quantity: 1, properties: props });
  }
  items.push({ id: Number(giftVariantId), quantity: notes.length, properties: { Per: productTitle, [FOR_VARIANT]: String(id) } });

  const payload = { items };
  if (sections.size) payload.sections = Array.from(sections).join(',');
  return payload;
}

function planSync(items, giftVariantId) {
  const gid = Number(giftVariantId);
  const list = items || [];
  const wrappedQty = {};
  const flaggedLines = [];

  for (const it of list) {
    if (Number(it.variant_id) === gid) continue;
    if (it.properties && it.properties[FLAG]) {
      wrappedQty[it.variant_id] = (wrappedQty[it.variant_id] || 0) + it.quantity;
      flaggedLines.push(it);
    }
  }

  const updates = {};
  const haveGift = new Set();
  for (const it of list) {
    if (Number(it.variant_id) !== gid) continue;
    const target = it.properties && it.properties[FOR_VARIANT];
    if (!target) continue;
    haveGift.add(String(target));
    const want = wrappedQty[target] || 0;
    if (want !== it.quantity) updates[it.key] = want;
  }

  // La confezione e' stata tolta ma l'articolo porta ancora "Confezione regalo: Sì": toglie anche quella richiesta.
  const clears = [];
  for (const it of flaggedLines) {
    if (haveGift.has(String(it.variant_id))) continue;
    // Shopify ignora un oggetto vuoto: per cancellare una proprieta' va mandata con valore vuoto.
    const props = { ...it.properties, [FLAG]: '', [NOTE]: '' };
    clears.push({ id: it.key, quantity: it.quantity, properties: props });
  }

  return { updates, clears };
}

function isWrappedMulti(it, giftVariantId) {
  return Number(it.variant_id) !== Number(giftVariantId) && !!(it.properties && it.properties[FLAG]) && it.quantity > 1;
}

async function splitWrapped(items, giftVariantId, post) {
  let changed = false;
  for (const it of items) {
    if (!isWrappedMulti(it, giftVariantId)) continue;
    const copies = [];
    for (let i = 1; i < it.quantity; i++) {
      copies.push({ id: it.variant_id, quantity: 1, properties: { ...it.properties, [ROW]: newToken() } });
    }
    // Prima riduce la riga a 1 (cosi' non supera la disponibilita'), poi aggiunge le altre; se fallisce, rimette com'era.
    const reduced = await post('/cart/change.js', { id: it.key, quantity: 1 });
    if (reduced.status || reduced.errors) continue;
    const added = await post('/cart/add.js', { items: copies });
    if (added.status || added.errors) {
      await post('/cart/change.js', { id: it.key, quantity: it.quantity });
      continue;
    }
    changed = true;
  }
  return changed;
}

// ---- DOM ----

const config = /** @type {{variantId: number} | undefined} */ (window.GiftWrapConfig);

if (config && config.variantId) {
  // Scheda prodotto: quante confezioni regalo (da 0 al numero di pezzi) e un biglietto per ognuna.
  const state = { count: 0, notes: /** @type {string[]} */ ([]) };
  const MAX_PIECES = 20;
  const priceText = config.priceLabel || '';

  const styleTag = document.createElement('style');
  styleTag.textContent = `
    .gw-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
    .gw-step { display: inline-flex; align-items: stretch; border: 1px solid color-mix(in srgb, currentColor 30%, transparent); border-radius: 0; }
    .gw-step button { width: 40px; height: 40px; border: 0; background: transparent; color: inherit; font: inherit; font-size: 1.125rem; cursor: pointer; border-radius: 0; }
    .gw-step button:disabled { opacity: 0.3; cursor: default; }
    .gw-step span { min-width: 36px; display: inline-flex; align-items: center; justify-content: center; }
    .gw-note { margin-top: 12px; display: flex; gap: 12px; align-items: flex-start; } .gw-body { flex: 1; min-width: 0; } .gw-pic { width: 64px; height: 85px; object-fit: cover; border-radius: 0; flex: none; }
  `;
  document.head.appendChild(styleTag);

  const quantityOf = (/** @type {Element} */ root) => {
    const qty = /** @type {HTMLInputElement | null} */ (root.closest('form')?.querySelector('input[name="quantity"]'));
    return Math.min(MAX_PIECES, Math.max(1, parseInt(qty?.value || '1', 10) || 1));
  };

  const render = (/** @type {Element} */ root) => {
    const toggle = /** @type {HTMLElement | null} */ (root.querySelector('.gift-wrap__toggle'));
    const card = /** @type {HTMLElement | null} */ (root.querySelector('[data-gift-wrap-card]'));
    const check = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-gift-wrap-check]'));
    const text = /** @type {HTMLTextAreaElement | null} */ (root.querySelector('[data-gift-wrap-text]'));
    if (!toggle || !card) return;
    // Il vecchio selettore con casella singola non serve piu': resta nascosto e non invia nulla.
    toggle.style.display = 'none';
    card.hidden = true;
    if (check) check.checked = false;
    if (text) text.disabled = true;

    const max = quantityOf(root);
    if (state.count > max) state.count = max;
    while (state.notes.length < state.count) state.notes.push('');

    let ui = /** @type {HTMLElement | null} */ (root.querySelector('[data-gift-wrap-ui]'));
    if (!ui) {
      ui = document.createElement('div');
      ui.setAttribute('data-gift-wrap-ui', '');
      ui.innerHTML = `
        <div class="gw-row">
          <span class="gift-wrap__label">Confezione regalo <strong>+${priceText}</strong> cad.</span>
          <div class="gw-step">
            <button type="button" data-gw-minus aria-label="Meno confezioni regalo">−</button>
            <span data-gw-num>0</span>
            <button type="button" data-gw-plus aria-label="Più confezioni regalo">+</button>
          </div>
        </div>
        <div data-gw-notes></div>`;
      root.appendChild(ui);
    }
    const num = ui.querySelector('[data-gw-num]');
    if (num) num.textContent = String(state.count);
    const minus = /** @type {HTMLButtonElement | null} */ (ui.querySelector('[data-gw-minus]'));
    const plus = /** @type {HTMLButtonElement | null} */ (ui.querySelector('[data-gw-plus]'));
    if (minus) minus.disabled = state.count <= 0;
    if (plus) plus.disabled = state.count >= max;

    const box = /** @type {HTMLElement} */ (ui.querySelector('[data-gw-notes]'));
    while (box.children.length > state.count) box.lastElementChild?.remove();
    while (box.children.length < state.count) {
      const idx = box.children.length;
      const wrap = document.createElement('div');
      wrap.className = 'gift-wrap__card gw-note';
      wrap.innerHTML = `<img class="gw-pic" src="https://cdn.shopify.com/s/files/1/1035/1987/9502/files/Regali_eleganti_firmati_Alessandro_Ricci.png?v=1792862981&width=200" alt="" loading="lazy"><div class="gw-body"><label class="gift-wrap__card-label"><span data-gw-note-title></span><textarea rows="3" maxlength="${MAX_NOTE}" placeholder="Scrivi qui il tuo messaggio" data-gw-note data-idx="${idx}"></textarea></label><span class="gift-wrap__count" data-gw-note-count>0/${MAX_NOTE}</span></div>`;
      box.appendChild(wrap);
    }
    Array.from(box.children).forEach((wrap, i) => {
      const title = wrap.querySelector('[data-gw-note-title]');
      if (title) title.textContent = state.count > 1 ? `Biglietto ${i + 1} di ${state.count}` : 'Testo del biglietto (facoltativo)';
      const area = /** @type {HTMLTextAreaElement} */ (wrap.querySelector('textarea'));
      const value = state.notes[i] || '';
      if (area.value !== value) area.value = value;
      const counter = wrap.querySelector('[data-gw-note-count]');
      if (counter) counter.textContent = `${area.value.length}/${MAX_NOTE}`;
    });
  };

  const renderAll = () => document.querySelectorAll('[data-gift-wrap]').forEach((root) => render(root));

  const resetUI = () => {
    state.count = 0;
    state.notes = [];
    renderAll();
  };

  const mount = () => {
    const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('gift-wrap-template'));
    if (!tpl) return;
    const productForm = document.querySelector(`product-form-component[data-product-id="${tpl.dataset.productId}"]`);
    const form = productForm?.querySelector('form');
    if (!form || form.querySelector('[data-gift-wrap]')) return;
    const anchor = form.querySelector('.product-form-buttons');
    if (!anchor) return;
    anchor.before(tpl.content.cloneNode(true));
    const root = form.querySelector('[data-gift-wrap]');
    if (root) render(root);
  };

  // La scheda puo' essere ridisegnata dal tema (es. cambio variante): rimonta il blocco se sparisce.
  let mountTimer = 0;
  const scheduleMount = () => {
    clearTimeout(mountTimer);
    mountTimer = window.setTimeout(mount, 50);
  };
  mount();
  new MutationObserver(scheduleMount).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', mount);

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const minus = target.closest?.('[data-gw-minus]');
    const plus = target.closest?.('[data-gw-plus]');
    if (minus || plus) {
      event.preventDefault();
      const root = target.closest('[data-gift-wrap]');
      if (!root) return;
      const max = quantityOf(root);
      state.count = Math.max(0, Math.min(max, state.count + (plus ? 1 : -1)));
      render(root);
      return;
    }
    if (target.closest?.('quantity-selector-component, .quantity-selector')) refreshPieces();
  });
  document.addEventListener('input', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches?.('[data-gw-note]')) {
      const area = /** @type {HTMLTextAreaElement} */ (target);
      state.notes[Number(area.dataset.idx)] = area.value;
      const counter = area.closest('.gw-note')?.querySelector('[data-gw-note-count]');
      if (counter) counter.textContent = `${area.value.length}/${MAX_NOTE}`;
      return;
    }
    if (target.matches?.('input[name="quantity"]')) refreshPieces();
  });
  document.addEventListener('change', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches?.('input[name="quantity"]')) refreshPieces();
  });
  document.addEventListener('keyup', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches?.('input[name="quantity"]')) refreshPieces();
  });

  // Cambia la quantita' nella scheda prodotto: il numero di confezioni non puo' superarla.
  function refreshPieces() {
    window.setTimeout(renderAll, 60);
  }

  // Aggiunta al carrello: articolo + confezione in un'unica richiesta (o entrambi o nessuno).
  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      if (init && init.body instanceof FormData && /\/cart\/add(\.js)?(\?|$)/.test(url)) {
        const title = document.getElementById('gift-wrap-template')?.dataset.productTitle || '';
        const payload = buildAddPayload(Array.from(init.body.entries()), config.variantId, title, state.notes.slice(0, state.count));
        if (payload) {
          return nativeFetch(input, {
            ...init,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
    return nativeFetch(input, init);
  };

  // Dopo ogni modifica del carrello, allinea la confezione agli articoli.
  let syncing = false;
  document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
    const detail = /** @type {any} */ (event).detail || {};
    const source = detail.data && detail.data.source;

    if (source === 'product-form-component') {
      if (!detail.data.didError) resetUI();
      return;
    }
    if (source === 'gift-wrap' || syncing) return;

    syncing = true;
    try {
      let cart = detail.resource;
      if (!cart || !Array.isArray(cart.items)) {
        cart = await (await nativeFetch('/cart.js')).json();
      }
      const plan = planSync(cart.items, config.variantId);
      const hasUpdates = Object.keys(plan.updates).length > 0;
      const needsSplit = cart.items.some((it) => isWrappedMulti(it, config.variantId));
      if (!hasUpdates && plan.clears.length === 0 && !needsSplit) return;

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
        .filter(Boolean)
        .join(',');
      const extra = sectionIds ? { sections: sectionIds, sections_url: window.location.pathname } : {};
      const post = async (/** @type {string} */ url, /** @type {object} */ body) =>
        (
          await nativeFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          })
        ).json();

      let last = null;
      if (hasUpdates) last = await post('/cart/update.js', { updates: plan.updates, ...extra });
      for (const change of plan.clears) {
        last = await post('/cart/change.js', { ...change, ...extra });
      }

      // Righe con confezione e quantita' > 1: una riga per pezzo, cosi' ogni biglietto e' separato.
      const current = last && Array.isArray(last.items) ? last : await post('/cart/update.js', { updates: {} });
      if (current.items.some((it) => isWrappedMulti(it, config.variantId))) {
        if (await splitWrapped(current.items, config.variantId, post)) {
          last = await post('/cart/update.js', { updates: {}, ...extra });
        }
      }

      if (last && !last.status && !last.errors) {
        document.dispatchEvent(
          new CartUpdateEvent(last, 'gift-wrap', {
            itemCount: last.item_count,
            source: 'gift-wrap',
            sections: last.sections,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      syncing = false;
    }
  });

  // ---- Carrello: "Aggiungi confezione regalo" sotto ogni articolo che non ce l'ha ----

  const priceLabel = config.priceLabel || '';
  const giftId = Number(config.variantId);

  const postJson = async (/** @type {string} */ url, /** @type {object} */ body) =>
    (
      await nativeFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
    ).json();

  const cartSections = () => {
    const ids = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean)
      .join(',');
    return ids ? { sections: ids, sections_url: window.location.pathname } : {};
  };

  /** @type {Array<any>} */
  let cartItems = [];
  let cartLoaded = false;
  const loadCart = async () => {
    const cart = await (await nativeFetch('/cart.js')).json();
    cartItems = cart.items || [];
    cartLoaded = true;
    return cart;
  };

  const cartBlockHtml = `
    <label class="gift-wrap__toggle">
      <input type="checkbox" data-gw-cart-check>
      <span class="gift-wrap__label">Aggiungi confezione regalo <strong data-gw-cart-price></strong></span>
    </label>
    <div class="gift-wrap__card" data-gw-cart-card hidden>
      <label class="gift-wrap__card-label">
        Testo del biglietto (facoltativo)
        <textarea rows="3" maxlength="${MAX_NOTE}" placeholder="Scrivi qui il tuo messaggio" data-gw-cart-text></textarea>
      </label>
      <span class="gift-wrap__count" data-gw-cart-count>0/${MAX_NOTE}</span>
      <button type="button" class="button gift-wrap__apply" data-gw-cart-apply>Aggiungi confezione</button>
      <p class="gift-wrap__error" data-gw-cart-error hidden></p>
    </div>`;

  const cartEditHtml = `
    <div class="gift-wrap__card">
      <label class="gift-wrap__card-label">
        Confezione regalo: testo del biglietto
        <textarea rows="3" maxlength="${MAX_NOTE}" placeholder="Scrivi qui il tuo messaggio" data-gw-cart-text></textarea>
      </label>
      <span class="gift-wrap__count" data-gw-cart-count>0/${MAX_NOTE}</span>
      <button type="button" class="button gift-wrap__apply" data-gw-cart-save>Salva biglietto</button>
      <button type="button" class="button gift-wrap__apply" data-gw-cart-remove>Togli confezione</button>
      <p class="gift-wrap__error" data-gw-cart-error hidden></p>
    </div>`;

  let decorating = false;
  const decorateCart = async () => {
    if (decorating) return;
    const rows = Array.from(document.querySelectorAll('cart-items-component tr[data-key]')).filter(
      (row) => !(/** @type {HTMLElement} */ (row).dataset.parentKey) && !row.querySelector('[data-gift-wrap-cart]')
    );
    if (rows.length === 0) return;

    decorating = true;
    try {
      if (!cartLoaded || rows.some((row) => !cartItems.some((it) => it.key === /** @type {HTMLElement} */ (row).dataset.key))) {
        await loadCart();
      }
      for (const row of rows) {
        const key = /** @type {HTMLElement} */ (row).dataset.key;
        const item = cartItems.find((it) => it.key === key);
        if (!item || Number(item.variant_id) === giftId) continue;
        const host = row.querySelector('.cart-items__details');
        if (!host) continue;
        const block = document.createElement('div');
        block.className = 'gift-wrap gift-wrap--cart';
        block.setAttribute('data-gift-wrap-cart', '');
        block.dataset.key = key;
        if (item.properties && item.properties[FLAG]) {
          // Articolo gia' con confezione: il biglietto resta modificabile, uno per ogni riga.
          block.innerHTML = cartEditHtml;
          const noteBox = /** @type {HTMLTextAreaElement | null} */ (block.querySelector('[data-gw-cart-text]'));
          const noteCount = block.querySelector('[data-gw-cart-count]');
          const current = String((item.properties && item.properties[NOTE]) || '');
          if (noteBox) noteBox.value = current;
          if (noteCount) noteCount.textContent = `${current.length}/${MAX_NOTE}`;
        } else {
          block.innerHTML = cartBlockHtml;
          const price = block.querySelector('[data-gw-cart-price]');
          if (price && priceLabel) price.textContent = `+${priceLabel}`;
        }
        host.appendChild(block);
      }
    } catch (error) {
      console.error(error);
    } finally {
      decorating = false;
    }
  };

  let decorateTimer = 0;
  const scheduleDecorate = () => {
    clearTimeout(decorateTimer);
    decorateTimer = window.setTimeout(decorateCart, 80);
  };
  decorateCart();
  new MutationObserver(scheduleDecorate).observe(document.body, { childList: true, subtree: true });
  // Il carrello cambia (altro articolo aggiunto, quantita'...): rileggi i dati alla prossima decorazione.
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    cartLoaded = false;
    scheduleDecorate();
  });

  document.addEventListener('change', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.matches?.('[data-gw-cart-check]')) return;
    const block = target.closest('[data-gift-wrap-cart]');
    const card = /** @type {HTMLElement | null} */ (block?.querySelector('[data-gw-cart-card]') || null);
    if (card) card.hidden = !(/** @type {HTMLInputElement} */ (target).checked);
  });
  document.addEventListener('input', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.matches?.('[data-gw-cart-text]')) return;
    const count = target.closest('[data-gift-wrap-cart]')?.querySelector('[data-gw-cart-count]');
    if (count) count.textContent = `${/** @type {HTMLTextAreaElement} */ (target).value.length}/${MAX_NOTE}`;
  });

  // Salva il testo del biglietto di una riga che ha gia' la confezione.
  document.addEventListener('click', async (event) => {
    const button = /** @type {HTMLButtonElement | null} */ ((/** @type {HTMLElement} */ (event.target)).closest?.('[data-gw-cart-save]'));
    if (!button) return;
    event.preventDefault();
    const block = /** @type {HTMLElement | null} */ (button.closest('[data-gift-wrap-cart]'));
    if (!block) return;
    const key = block.dataset.key;
    const text = /** @type {HTMLTextAreaElement | null} */ (block.querySelector('[data-gw-cart-text]'));
    const error = /** @type {HTMLElement | null} */ (block.querySelector('[data-gw-cart-error]'));
    const fail = (/** @type {string} */ message) => {
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };
    if (error) error.hidden = true;
    button.disabled = true;
    syncing = true;
    try {
      await loadCart();
      const item = cartItems.find((it) => it.key === key);
      if (!item) return fail("Non trovo l'articolo nel carrello. Ricarica la pagina.");
      const note = (text?.value || '').trim().slice(0, MAX_NOTE);
      const props = { ...(item.properties || {}), [NOTE]: note };
      const changed = await postJson('/cart/change.js', { id: key, quantity: item.quantity, properties: props, ...cartSections() });
      if (changed.status || changed.errors) return fail('Non sono riuscito a salvare il biglietto. Riprova.');
      document.dispatchEvent(
        new CartUpdateEvent(changed, 'gift-wrap', { itemCount: changed.item_count, source: 'gift-wrap', sections: changed.sections })
      );
    } catch (err) {
      console.error(err);
      fail('Qualcosa non ha funzionato. Riprova.');
    } finally {
      button.disabled = false;
      syncing = false;
    }
  });

  // Toglie la confezione da una riga (e la relativa riga "Confezione regalo" si riduce da sola).
  document.addEventListener('click', async (event) => {
    const button = /** @type {HTMLButtonElement | null} */ ((/** @type {HTMLElement} */ (event.target)).closest?.('[data-gw-cart-remove]'));
    if (!button) return;
    event.preventDefault();
    const block = /** @type {HTMLElement | null} */ (button.closest('[data-gift-wrap-cart]'));
    if (!block) return;
    const key = block.dataset.key;
    const error = /** @type {HTMLElement | null} */ (block.querySelector('[data-gw-cart-error]'));
    const fail = (/** @type {string} */ message) => {
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };
    if (error) error.hidden = true;
    button.disabled = true;
    syncing = true;
    try {
      await loadCart();
      const item = cartItems.find((it) => it.key === key);
      if (!item) return fail("Non trovo l'articolo nel carrello. Ricarica la pagina.");
      const props = { ...(item.properties || {}), [FLAG]: '', [NOTE]: '', [ROW]: '' };
      const changed = await postJson('/cart/change.js', { id: key, quantity: item.quantity, properties: props });
      if (changed.status || changed.errors) return fail('Non sono riuscito a togliere la confezione. Riprova.');
      const plan = planSync(changed.items, giftId);
      const cart = await postJson('/cart/update.js', { updates: plan.updates, ...cartSections() });
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'gift-wrap', { itemCount: cart.item_count, source: 'gift-wrap', sections: cart.sections })
      );
    } catch (err) {
      console.error(err);
      fail('Qualcosa non ha funzionato. Riprova.');
    } finally {
      button.disabled = false;
      syncing = false;
    }
  });

  document.addEventListener('click', async (event) => {
    const button = /** @type {HTMLElement | null} */ ((/** @type {HTMLElement} */ (event.target)).closest?.('[data-gw-cart-apply]'));
    if (!button) return;
    event.preventDefault();
    const block = /** @type {HTMLElement | null} */ (button.closest('[data-gift-wrap-cart]'));
    if (!block) return;
    const key = block.dataset.key;
    const text = /** @type {HTMLTextAreaElement | null} */ (block.querySelector('[data-gw-cart-text]'));
    const error = /** @type {HTMLElement | null} */ (block.querySelector('[data-gw-cart-error]'));
    const fail = (/** @type {string} */ message) => {
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };
    if (error) error.hidden = true;
    /** @type {HTMLButtonElement} */ (button).disabled = true;
    syncing = true;

    try {
      await loadCart();
      const item = cartItems.find((it) => it.key === key);
      if (!item) return fail("Non trovo l'articolo nel carrello. Ricarica la pagina.");

      const note = (text?.value || '').trim().slice(0, MAX_NOTE);
      const wrapProps = { ...(item.properties || {}), [FLAG]: 'Sì', [ROW]: newToken() };
      if (note) wrapProps[NOTE] = note;
      let rowToken = '';

      if (item.quantity > 1) {
        // La riga ha piu' pezzi: ne confeziona uno solo, gli altri restano senza.
        const reduced = await postJson('/cart/change.js', { id: key, quantity: item.quantity - 1 });
        if (reduced.status || reduced.errors) return fail('Non sono riuscito ad aggiungere la confezione. Riprova.');
        const wrappedLine = await postJson('/cart/add.js', { items: [{ id: item.variant_id, quantity: 1, properties: wrapProps }] });
        if (wrappedLine.status || wrappedLine.errors) {
          await postJson('/cart/change.js', { id: key, quantity: item.quantity });
          return fail('Non sono riuscito ad aggiungere la confezione. Riprova.');
        }
        rowToken = wrapProps[ROW];
      } else {
        const changed = await postJson('/cart/change.js', { id: key, quantity: 1, properties: wrapProps });
        if (changed.status || changed.errors) return fail('Non sono riuscito ad aggiungere la confezione. Riprova.');
        rowToken = wrapProps[ROW];
      }

      const added = await postJson('/cart/add.js', {
        items: [{ id: giftId, quantity: 1, properties: { Per: item.product_title, [FOR_VARIANT]: String(item.variant_id) } }],
        ...cartSections(),
      });
      if (added.status) {
        // La confezione non e' stata aggiunta: toglie la richiesta dal pezzo, cosi' non resta senza addebito.
        const current = await loadCart();
        const line = current.items.find((it) => it.properties && it.properties[ROW] === rowToken);
        if (line) {
          const undo = { ...line.properties, [FLAG]: '', [NOTE]: '', [ROW]: '' };
          await postJson('/cart/change.js', { id: line.key, quantity: line.quantity, properties: undo });
        }
        return fail(added.description || added.message || 'La confezione non e\' disponibile in questo momento.');
      }

      let cart = await loadCart();
      let sections = added.sections;
      if (await splitWrapped(cart.items, giftId, postJson)) {
        cart = await postJson('/cart/update.js', { updates: {}, ...cartSections() });
        sections = cart.sections;
      }
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'gift-wrap', { itemCount: cart.item_count, source: 'gift-wrap', sections })
      );
    } catch (err) {
      console.error(err);
      fail('Qualcosa non ha funzionato. Riprova.');
    } finally {
      /** @type {HTMLButtonElement} */ (button).disabled = false;
      syncing = false;
    }
  });
}
